import { Field } from '@eon-home/react-library';
import { useTranslation } from 'react-i18next';
import { UseFormRegister, FieldValues, FieldErrors } from 'react-hook-form';

import { TimePicker } from '@containers/emobility';
import {
    CommonChargingInputValue,
    SmartOrProgramChargingFieldName,
    SmartOrProgramChargingFields,
} from '@containers/emobility/charging-modes/shared/common-charging-prompt/types';
import { Dispatch, SetStateAction, FC, ChangeEvent } from 'react';

import './index.scss';

interface Props {
    name: SmartOrProgramChargingFieldName;
    title: string;
    hint?: string;
    register: UseFormRegister<SmartOrProgramChargingFields>;
    isTimePicker?: boolean;
    value: CommonChargingInputValue;
    setValue: Dispatch<SetStateAction<CommonChargingInputValue>>;
    errors: FieldErrors<FieldValues>;
    minValue?: number;
    maxValue?: number;
    isReadOnly?: boolean;
}

const DEFAULT_MIN_VALUE = 0;
const DEFAULT_MAX_VALUE = 100;

const CommonChargingPromptSettingInput: FC<Readonly<Props>> = ({
    hint,
    register,
    isTimePicker,
    name,
    value,
    setValue,
    errors,
    minValue = DEFAULT_MIN_VALUE,
    maxValue = DEFAULT_MAX_VALUE,
    isReadOnly = false,
}) => {
    const { t } = useTranslation();

    if (isTimePicker && typeof value === 'string') {
        return <TimePicker value={value} action={setValue} />;
    }

    return (
        <Field
            className="charging-setting"
            hint={hint}
            register={register(name, {
                value,
                min: {
                    value: minValue,
                    message: t(
                        'Please use a whole number between {{ min }} and {{ max }}',
                        {
                            min: minValue,
                            max: maxValue,
                        },
                    ),
                },
                max: {
                    value: maxValue,
                    message: t(
                        'Please use a whole number between {{ min }} and {{ max }}',
                        {
                            min: minValue,
                            max: maxValue,
                        },
                    ),
                },
                onChange: (event: ChangeEvent<HTMLInputElement>) =>
                    setValue(event.target.value),
                required: t('This field is required.'),
            })}
            type="number"
            name={name}
            error={errors[name]}
            disabled={isReadOnly}
        />
    );
};

const CommonChargingPromptSetting: FC<Readonly<Props>> = ({
    title,
    ...rest
}) => (
    <>
        <h4>{title}</h4>
        <CommonChargingPromptSettingInput title={title} {...rest} />
    </>
);

export default CommonChargingPromptSetting;
